// `SidePeek` (design-system §3.12, spec 09 §4.15, D-314) — a record beside
// its list, with the list still working behind it.
//
// WHAT IT GUARANTEES
//  * A non-modal `role="dialog"` with `aria-modal="false"`, named by its
//    heading. It holds NO focus trap and paints no scrim, because nothing
//    behind it is blocked: the list sorts, pages and opens rows while the
//    peek is open (D-314 ruling 1). Everything else about it is `Modal`'s
//    grammar: a head row with the heading and a labelled ✕.
//  * The heading takes focus when the peek opens, and again when it switches
//    to another record. It is one element for the panel's whole life, so a
//    record resolving under it never moves focus.
//  * Escape closes it from anywhere on the page, heard in the BUBBLE phase.
//    `Modal` listens in the capture phase and stops the event, so a Modal
//    opened inside the peek traps and closes on its own first, and the peek
//    stays. An Escape another control already consumed (`preventDefault`),
//    or one pressed mid-composition, is left alone.
//  * Focus goes back when it closes: to the control that opened it, else to
//    the control on the page carrying the record's `data-peek-key` (how a
//    link's arrival, which no control opened, finds the row's Peek button),
//    else to the row carrying `data-peek-row` when it can take focus, else to
//    the page heading. It goes back when the reader closed the peek, or when
//    focus was inside the panel as it went (the Back button). When the reader
//    had already moved focus into the list, it stays there.
//  * The states render inside the panel: a skeleton reserving the header and
//    two panels while the record loads (§5), one sentence with Close for a
//    record the reader cannot open, an error with Retry and the request id,
//    and the offline sentence. The record's own panels show only when ready.
//  * i18n-free: every word arrives translated from the caller.
//
// The panel renders where the screen renders it, not through a portal, so a
// screen's first-paint test (`renderToStaticMarkup`) can render it. It is
// `position: fixed` with no transform at rest (styles/office.css), so it
// escapes any scrolling ancestor, and a Modal's fixed scrim inside it still
// covers the viewport.
//
// `usePeekAddress` is the address half (D-314 rulings 2 and 4): it reads
// `peek` from the address and writes it by the rules in `peek-address.ts`.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { Button } from "./button";
import {
  PEEK_PUSHED_STATE,
  SIDE_PEEK_ID,
  parsePeek,
  peekSearchParam,
  planPeekClose,
  planPeekOpen,
  withPeek,
  type PeekTarget,
  type PeekWrite,
} from "./peek-address";
import { ErrorState, OfflineState, Skeleton } from "./states";
import { useDesktop } from "./use-desktop";

/** What the panel shows. Each state carries its own words. */
export type SidePeekState =
  | {
      readonly status: "loading";
      /** Announced while the skeleton shows ("Loading the quote"). */
      readonly label: string;
    }
  | { readonly status: "ready" }
  | {
      readonly status: "unavailable";
      /** "This record is not available to you" (a 403 or a 404). */
      readonly message: string;
    }
  | {
      readonly status: "error";
      readonly title: string;
      readonly detail?: string | undefined;
      readonly retryLabel: string;
      readonly onRetry: () => void;
      readonly requestId?: string | undefined;
      readonly requestIdLabel?: string | undefined;
    }
  | { readonly status: "offline"; readonly title: string; readonly body: string };

export interface SidePeekProps {
  /** The record shown, in its address form (`<kind>:<uuid>`). A new key is a
   * switch: the panel stays and its heading takes focus again. */
  peekKey: string;
  /** The panel's name while it has no visible heading ("Quote"). */
  label: string;
  /** The visible heading once the record reads ("Q-0012"). */
  heading?: ReactNode;
  /** The ✕'s accessible name, and the word on the unavailable state's button. */
  closeLabel: string;
  onClose: () => void;
  state: SidePeekState;
  /** The record's panels, shown when `state` is ready. */
  children?: ReactNode;
  testId?: string | undefined;
}

/** The first candidate that actually takes focus. `focus()` on a disabled or
 * hidden control does nothing, so each one is checked, not assumed. */
function focusFirst(candidates: readonly (HTMLElement | null | undefined)[]): boolean {
  for (const el of candidates) {
    if (!el?.isConnected) continue;
    el.focus();
    if (document.activeElement === el) return true;
  }
  return false;
}

function byData(attribute: "data-peek-key" | "data-peek-row", keys: readonly string[]) {
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[${attribute}]`));
  return keys.flatMap((key) => all.filter((el) => el.getAttribute(attribute) === key));
}

/** Gives focus back as the header describes: the opener, the record's Peek
 * control, its row, the page heading. It runs once the panel has left the
 * document, so nothing that was inside the panel can be chosen: a query no
 * longer finds it, and `focusFirst` skips a node that is not connected. */
function returnFocus(opener: HTMLElement | null, keys: readonly string[]): void {
  if (focusFirst([opener])) return;
  if (focusFirst(byData("data-peek-key", keys))) return;
  if (focusFirst(byData("data-peek-row", keys).filter((el) => el.hasAttribute("tabindex")))) {
    return;
  }
  const heading = document.querySelector<HTMLElement>("h1");
  if (heading === null) return;
  if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
  heading.focus();
}

export function SidePeek({
  peekKey,
  label,
  heading,
  closeLabel,
  onClose,
  state,
  children,
  testId,
}: SidePeekProps) {
  const headingId = useId();
  const panel = useRef<HTMLElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  // The control outside the panel that opened or last switched it.
  const opener = useRef<HTMLElement | null>(null);
  // The keys focus can be returned by: the one shown now, then the first.
  const firstKey = useRef(peekKey);
  const shownKey = useRef(peekKey);
  shownKey.current = peekKey;
  // Set when the reader closes it through the peek itself.
  const explicit = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;

  // Open and switch. Runs before paint, so focus is in the panel the frame
  // it appears.
  useLayoutEffect(() => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      !(panel.current?.contains(active) ?? false)
    ) {
      opener.current = active;
    }
    title.current?.focus({ preventScroll: true });
  }, [peekKey]);

  // Close, in two steps. The layout cleanup runs while the panel is still in
  // the document, so it can tell whether focus is inside it before it goes.
  // The focus itself moves in the passive cleanup, after the commit: React
  // puts focus back on whatever held it before a commit when that element
  // survives it, so a move made during the commit is undone whenever the
  // reader had focus in the list (an Escape pressed on a sort header).
  const focusInside = useRef(false);
  useLayoutEffect(
    () => () => {
      const el = panel.current;
      const active = document.activeElement;
      focusInside.current = el !== null && active !== null && el.contains(active);
    },
    [],
  );
  useEffect(
    () => () => {
      const active = document.activeElement;
      const lost = active === null || active === document.body;
      if (!explicit.current && !focusInside.current && !lost) return;
      returnFocus(opener.current, [shownKey.current, firstKey.current]);
    },
    [],
  );

  // Escape, in the bubble phase (see the header).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return;
      event.preventDefault();
      explicit.current = true;
      close.current();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const closeNow = (): void => {
    explicit.current = true;
    onClose();
  };

  const loading = state.status === "loading";

  let body: ReactNode;
  switch (state.status) {
    case "loading":
      body = (
        <div
          className="ds-peek-skel"
          role="status"
          aria-label={state.label}
          data-testid="side-peek-skeleton"
        >
          <div className="ds-peek-skel-meta">
            <Skeleton width="45%" height="0.9rem" />
          </div>
          {[0, 1].map((i) => (
            <div className="ds-peek-skel-panel" key={i}>
              <Skeleton width="35%" height="0.9rem" />
              <Skeleton height="0.9rem" />
              <Skeleton height="0.9rem" />
              <Skeleton width="70%" height="0.9rem" />
            </div>
          ))}
        </div>
      );
      break;
    case "ready":
      body = children;
      break;
    case "unavailable":
      body = (
        <div className="ds-peek-note" data-testid="side-peek-unavailable">
          <p className="ds-peek-note-text">{state.message}</p>
          <Button onClick={closeNow} data-testid="side-peek-unavailable-close">
            {closeLabel}
          </Button>
        </div>
      );
      break;
    case "error":
      body = (
        <ErrorState
          title={state.title}
          detail={state.detail}
          retryLabel={state.retryLabel}
          onRetry={state.onRetry}
          requestId={state.requestId}
          requestIdLabel={state.requestIdLabel}
        />
      );
      break;
    case "offline":
      body = <OfflineState title={state.title} body={state.body} />;
      break;
  }

  return (
    <section
      ref={panel}
      id={SIDE_PEEK_ID}
      className="ds-peek"
      role="dialog"
      aria-modal="false"
      aria-labelledby={headingId}
      {...(loading ? { "aria-busy": true } : {})}
      data-peek={peekKey}
      data-testid={testId ?? "side-peek"}
    >
      <div className="ds-peek-head">
        <h2
          id={headingId}
          ref={title}
          className="ds-peek-title"
          tabIndex={-1}
          data-testid="side-peek-heading"
        >
          {heading ??
            (loading ? (
              <>
                <span className="visually-hidden">{label}</span>
                <Skeleton width="60%" height="1.1rem" />
              </>
            ) : (
              label
            ))}
        </h2>
        <button
          type="button"
          className="ds-peek-close"
          aria-label={closeLabel}
          data-testid="side-peek-close"
          onClick={closeNow}
        >
          ✕
        </button>
      </div>
      <div className="ds-peek-body">{body}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The address half
// ---------------------------------------------------------------------------

export interface PeekAddressOptions {
  /**
   * Opens the record's own page. Below 769 px no list offers a peek, and an
   * address that carries one lands on the record's own route (D-314 ruling
   * 4): the hook first replaces the list's entry with one without `peek`, so
   * Back from the record returns to the list and not into a loop, and then
   * calls this, which pushes the record's route.
   */
  readonly openRecord: (target: PeekTarget) => void;
}

export interface PeekAddress {
  /** The open peek's address form, from 769 px up; undefined when none is
   * open and always on a phone. Pass it to `DataTable`'s `peek.current`. */
  readonly code: string | undefined;
  /** The same, parsed: which record to read and render. */
  readonly target: PeekTarget | undefined;
  /** Whether this width offers a peek at all (from 769 px up). A control
   * outside `DataTable` that opens a peek renders only when this holds. */
  readonly offered: boolean;
  /** Opens a record's peek, or switches the open one to it. Opening from
   * closed pushes an entry, switching replaces it, and a malformed code or
   * the record already open writes nothing. */
  readonly open: (code: string) => void;
  /** Closes the peek: back when the app pushed the entry, else in place. */
  readonly close: () => void;
}

/** A navigation that stays on the current route and rewrites its search. */
interface StayOptions {
  to: ".";
  search: Record<string, unknown>;
  replace: boolean;
  state?: true | Record<string, unknown>;
}

/** The hook runs on whichever list renders it, so no one route's typed search
 * can be named here; the search it writes is the one it read, with only
 * `peek` changed, and each screen's `validateSearch` still reads it. */
function stay(navigate: ReturnType<typeof useNavigate>, options: StayOptions): Promise<void> {
  return Promise.resolve(navigate(options as unknown as Parameters<typeof navigate>[0]));
}

/** Reads and writes `peek=<kind>:<uuid>` on the current route (D-314 rulings
 * 2 and 4), keeping every other key of the address as it is. */
export function usePeekAddress({ openRecord }: PeekAddressOptions): PeekAddress {
  const navigate = useNavigate();
  const router = useRouter();
  const search: Record<string, unknown> = useSearch({ strict: false });
  const desktop = useDesktop();
  const addressed = peekSearchParam(search["peek"]);
  const code = desktop ? addressed : undefined;

  const searchRef = useRef(search);
  searchRef.current = search;
  const openRecordRef = useRef(openRecord);
  openRecordRef.current = openRecord;
  // The peek the last write asked for, so a second press before the address
  // lands (a double click, an Escape pressed twice) is judged against what is
  // coming and not against what was.
  const latest = useRef(code);
  useLayoutEffect(() => {
    latest.current = code;
  }, [code]);

  const write = useCallback(
    (plan: PeekWrite): void => {
      if (plan.kind === "none") return;
      latest.current = plan.kind === "back" ? undefined : plan.peek;
      if (plan.kind === "back") {
        router.history.back();
        return;
      }
      const options: StayOptions = {
        to: ".",
        search: withPeek(searchRef.current, plan.peek),
        replace: plan.replace,
      };
      if (plan.mark === "set") options.state = { [PEEK_PUSHED_STATE]: true };
      else if (plan.mark === "keep") options.state = true;
      void stay(navigate, options);
    },
    [navigate, router],
  );

  const open = useCallback(
    (next: string): void => {
      if (!desktop) return;
      write(planPeekOpen(latest.current, next));
    },
    [desktop, write],
  );

  const close = useCallback((): void => {
    write(planPeekClose(latest.current, router.history.location.state));
  }, [router, write]);

  // A phone that arrives on an address carrying a peek.
  const redirected = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (desktop || addressed === undefined || redirected.current === addressed) return;
    redirected.current = addressed;
    const target = parsePeek(addressed);
    if (target === undefined) return;
    const options: StayOptions = {
      to: ".",
      search: withPeek(searchRef.current, undefined),
      replace: true,
    };
    void stay(navigate, options).then(() => {
      openRecordRef.current(target);
    });
  }, [addressed, desktop, navigate]);

  return {
    code,
    target: code === undefined ? undefined : parsePeek(code),
    offered: desktop,
    open,
    close,
  };
}
